import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2,
  RefreshCw,
  Download,
  Eye,
  FolderOpen,
  MoveHorizontal,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { FileUploadZone } from './FileUploadZone';
import { FilePreviewDialog } from './FilePreviewDialog';
import FolderManager from './FolderManager';
import CreateFolderDialog from './CreateFolderDialog';
import MoveFileDialog from './MoveFileDialog';
import { format } from 'date-fns';
import { useUserRoles } from '@/hooks/useUserRoles';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

interface DataRoomProps {
  companyId: string;
}

export default function DataRoom({ companyId }: DataRoomProps) {
  const { isOwner, isAdmin } = useUserRoles(companyId);
  const [files, setFiles] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [category, setCategory] = useState('Corporate');
  const [visibility, setVisibility] = useState('INTERNAL');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [moveFileOpen, setMoveFileOpen] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  async function loadFolders() {
    const { data, error } = await supabase
      .from('data_room_folders')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true });
    if (error) toast.error(error.message);
    setFolders(data || []);
  }

  async function loadFiles() {
    setLoading(true);
    let query = supabase.from('data_room_files').select('*').eq('company_id', companyId);

    if (selectedFolderId) {
      query = query.eq('folder_id', selectedFolderId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    setFiles(data || []);
    setLoading(false);
  }

  async function handleFilesSelected(selectedFiles: File[]) {
    if (!companyId || selectedFiles.length === 0) {
      toast.error('No company selected or no files chosen');
      return;
    }

    setUploading(true);
    try {
      // Get user once before loop
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error('You must be logged in to upload files');
        setUploading(false);
        return;
      }

      // Check if user has access to this company
      const { data: userCompanies } = await supabase.rpc('user_companies', { _user_id: user.id });

      if (!userCompanies?.includes(companyId)) {
        toast.error(
          `You don't have access to upload files for this company. Please contact an administrator.`
        );
        setUploading(false);
        return;
      }

      // Validate visibility permissions
      const isOwnerOrAdmin = isOwner(companyId) || isAdmin(companyId);
      if (!isOwnerOrAdmin && visibility !== 'INTERNAL') {
        toast.error(
          'Only owners and admins can upload files with Board, Investor, or Public visibility'
        );
        setUploading(false);
        return;
      }

      let successCount = 0;
      const errors: string[] = [];

      for (const file of selectedFiles) {
        const path = `${companyId}/${Date.now()}-${Math.random().toString(36).substring(7)}-${file.name}`;

        const { error: upErr } = await supabase.storage.from('data-room').upload(path, file);

        if (upErr) {
          console.error(`Failed to upload ${file.name}:`, upErr);
          errors.push(`${file.name}: ${upErr.message}`);
          continue;
        }

        const { error } = await supabase.from('data_room_files').insert({
          company_id: companyId,
          file_name: file.name,
          file_url: path,
          category,
          visibility,
          size_bytes: file.size,
          mime_type: file.type,
          folder_id: selectedFolderId,
          uploaded_by: user.id,
        });

        if (error) {
          console.error(`Failed to save metadata for ${file.name}:`, error);
          errors.push(`${file.name}: ${error.message}`);
          continue;
        }

        successCount++;
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} of ${selectedFiles.length} files`);
        loadFiles();
      }

      if (errors.length > 0) {
        toast.error(`Upload errors: ${errors[0]}`);
        console.error('All upload errors:', errors);
      }
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
      console.error('Upload exception:', err);
    } finally {
      setUploading(false);
    }
  }

  async function downloadFile(fileUrl: string, fileName: string) {
    try {
      const { data, error } = await supabase.storage.from('data-room').download(fileUrl);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  async function deleteFile(fileId: string, fileUrl: string, fileName: string) {
    if (!confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      // Delete from database first
      const { error: dbError } = await supabase.from('data_room_files').delete().eq('id', fileId);

      if (dbError) throw dbError;

      // Delete from storage
      const { error: storageError } = await supabase.storage.from('data-room').remove([fileUrl]);

      if (storageError) {
        console.warn('Storage deletion warning:', storageError);
        // Don't throw - file might already be deleted
      }

      toast.success(`Successfully deleted ${fileName}`);
      loadFiles();
    } catch (err: any) {
      toast.error(`Failed to delete file: ${err.message}`);
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  }

  async function deleteSelectedFiles() {
    if (selectedFileIds.size === 0) return;

    if (
      !confirm(
        `Are you sure you want to delete ${selectedFileIds.size} file(s)? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      const filesToDelete = files.filter((f) => selectedFileIds.has(f.id));
      const fileUrls = filesToDelete.map((f) => f.file_url);

      // Delete from database
      const { error: dbError } = await supabase
        .from('data_room_files')
        .delete()
        .in('id', Array.from(selectedFileIds));

      if (dbError) throw dbError;

      // Delete from storage
      if (fileUrls.length > 0) {
        const { error: storageError } = await supabase.storage.from('data-room').remove(fileUrls);

        if (storageError) {
          console.warn('Storage deletion warning:', storageError);
          // Don't throw - some files might already be deleted
        }
      }

      toast.success(`Successfully deleted ${selectedFileIds.size} file(s)`);
      setSelectedFileIds(new Set());
      loadFiles();
    } catch (err: any) {
      toast.error(`Failed to delete files: ${err.message}`);
      console.error('Bulk delete error:', err);
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    loadFolders();
    loadFiles();
  }, [companyId, selectedFolderId]);

  const getFolderPath = (folderId: string | null): any[] => {
    if (!folderId) return [];
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return [];
    const path = folder.parent_id ? getFolderPath(folder.parent_id) : [];
    return [...path, folder];
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFileIds(new Set(files.map((f) => f.id)));
    } else {
      setSelectedFileIds(new Set());
    }
  };

  const handleSelectFile = (fileId: string, checked: boolean) => {
    const newSelection = new Set(selectedFileIds);
    if (checked) {
      newSelection.add(fileId);
    } else {
      newSelection.delete(fileId);
    }
    setSelectedFileIds(newSelection);
  };

  const folderPath = getFolderPath(selectedFolderId);

  return (
    <div className="grid grid-cols-[280px_1fr] gap-4 h-[calc(100vh-200px)]">
      <FolderManager
        folders={folders}
        selectedFolderId={selectedFolderId}
        onFolderSelect={setSelectedFolderId}
        onCreateFolder={() => setCreateFolderOpen(true)}
        onFoldersChange={() => {
          loadFolders();
          loadFiles();
        }}
      />

      <Card className="border-border bg-card overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex-1">
            <CardTitle className="mb-2">Data Room</CardTitle>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedFolderId(null);
                    }}
                    className="flex items-center gap-1"
                  >
                    <FolderOpen className="h-3 w-3" />
                    All Files
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {folderPath.map((folder, index) => (
                  <>
                    <BreadcrumbSeparator key={`sep-${folder.id}`} />
                    <BreadcrumbItem key={folder.id}>
                      <BreadcrumbLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedFolderId(folder.id);
                        }}
                      >
                        {folder.name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="flex gap-2">
            {selectedFileIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMoveFileOpen(true)}
                  disabled={deleting}
                >
                  <MoveHorizontal className="h-4 w-4 mr-1" />
                  Move ({selectedFileIds.size})
                </Button>
                {(isOwner(companyId) || isAdmin(companyId)) && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={deleteSelectedFiles}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    Delete ({selectedFileIds.size})
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" size="sm" onClick={loadFiles} disabled={deleting}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Corporate">Corporate</SelectItem>
                  <SelectItem value="Financials">Financials</SelectItem>
                  <SelectItem value="Legal">Legal</SelectItem>
                  <SelectItem value="Tax">Tax</SelectItem>
                  <SelectItem value="Compliance">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Visibility</label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL">Internal - Owners & Admins only</SelectItem>
                  <SelectItem value="BOARD">Board - Owners, Admins & Board Observers</SelectItem>
                  <SelectItem value="INVESTOR">Investor - Includes all Investors</SelectItem>
                  <SelectItem value="PUBLIC">Public - All company members</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <FileUploadZone
            onFilesSelected={handleFilesSelected}
            multiple={true}
            disabled={uploading}
            maxSize={50}
            data-tour="upload-zone"
          />

          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="animate-spin text-muted-foreground" />
            </div>
          ) : files.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedFileIds.size === files.length && files.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedFileIds.has(f.id)}
                        onCheckedChange={(checked) => handleSelectFile(f.id, checked as boolean)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{f.file_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{f.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{f.visibility}</Badge>
                    </TableCell>
                    <TableCell>
                      {f.size_bytes ? `${(f.size_bytes / 1024).toFixed(1)} KB` : '—'}
                    </TableCell>
                    <TableCell>{format(new Date(f.created_at), 'PP')}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setPreviewFile(f);
                            setPreviewOpen(true);
                          }}
                          disabled={deleting}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFile(f.file_url, f.file_name)}
                          disabled={deleting}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {(isOwner(companyId) || isAdmin(companyId)) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteFile(f.id, f.file_url, f.file_name)}
                            disabled={deleting}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">
              No files uploaded yet. Upload your first document to get started.
            </p>
          )}
        </CardContent>

        <FilePreviewDialog
          file={previewFile}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          bucketName="data-room"
        />
      </Card>

      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        companyId={companyId}
        folders={folders}
        onFolderCreated={() => {
          loadFolders();
          loadFiles();
        }}
      />

      <MoveFileDialog
        open={moveFileOpen}
        onOpenChange={setMoveFileOpen}
        fileIds={Array.from(selectedFileIds)}
        folders={folders}
        onFileMoved={() => {
          setSelectedFileIds(new Set());
          loadFiles();
        }}
      />
    </div>
  );
}
